import { useEffect, useRef } from "react";

/**
 * Scroll to the element named by `location.hash`, once the shell has rendered it.
 *
 * A browser resolves a fragment while parsing the document it arrived with. `dealbid-web`
 * is a static shell that renders its page in JavaScript, so at that moment `#tomorrow` does
 * not exist yet — the browser finds nothing, gives up, and never tries again. The reader
 * asked for a section and got the top of the page.
 *
 * That matters here because canon decision `D-006` moved four sections to `/about` and kept
 * the ids they already had, precisely so links written against them would keep working.
 * A fragment the browser silently drops would make that promise hollow, which is why
 * `e2e/tests/web-homepage.spec.ts` asserts the landing position rather than merely that the
 * id is present in the DOM — the id was present the whole time this was broken.
 *
 * `settled` is whatever the page is still waiting for. The scroll runs on mount and once
 * more when that flips, because content arriving above the target moves it: on `/about`
 * the explanation grows a figure block when the board lands. After the second attempt it
 * stops, so this can never fight a reader who has started scrolling for themselves.
 *
 * `scrollIntoView` is called with `behavior: "instant"` (`WEB-031`). It used to be called
 * with no options at all, which meant the same thing — until `src/styles/base.css` gave the
 * document `scroll-behavior: smooth`, at which point "no options" would have started
 * animating the *arrival* on a page the reader has not seen yet. A link followed within the
 * page animates, because there the motion shows the reader where they went; a fresh load
 * that already names a fragment simply starts there.
 *
 * The resting position is the same either way: `scroll-padding-block-start` is a property of
 * the scrolling element, so it applies to a programmatic scroll exactly as it does to a
 * fragment the browser resolves itself. And because this path never animates, it needs no
 * `prefers-reduced-motion` branch — the base stylesheet's override covers the links.
 */
export function useHashTarget(settled: boolean): void {
  const finished = useRef(false);

  useEffect(() => {
    if (finished.current) return;

    const id = decodeURIComponent(window.location.hash.replace(/^#/, ""));
    if (id === "") {
      finished.current = true;
      return;
    }

    const target = document.getElementById(id);
    // Not rendered yet — leave `finished` alone so the next attempt still happens. A hash
    // naming nothing at all simply never resolves, exactly as it would on a static page.
    if (target === null) return;

    target.scrollIntoView({ behavior: "instant", block: "start" });
    if (settled) finished.current = true;
  }, [settled]);
}
